import { Box, Button, Flex, Input, Select, Tooltip } from "@chakra-ui/react";
import {
  NegociacaoStatusEnum,
  OrdenacaoVinculosEmpregador,
  RenegociacaoVinculoEncerrado,
} from "@/models/consignado/proposal-billings.model";

export type VinculosFiltrosDraft = {
  tomador: string;
  empresa: string;
  ccb: string;
  ordenacao: "ASC" | "DESC";
  ordenarPor?: OrdenacaoVinculosEmpregador;
  // "" = todos os status.
  statusNegociacao?: NegociacaoStatusEnum | "";
  renegociacao?: RenegociacaoVinculoEncerrado | "";
};

const ROTULOS_ORDENACAO: Record<OrdenacaoVinculosEmpregador, Record<"ASC" | "DESC", string>> = {
  data_contratacao: { DESC: "Mais recentes", ASC: "Mais antigas" },
  competencia: { DESC: "Mais recentes", ASC: "Mais antigas" },
};

const selectStyle = {
  w: "fit-content",
  bg: "white",
  color: "black",
  fontFamily: "Lexend Deca",
  sx: { option: { color: "black", bg: "white" } },
};

type Props = {
  filtrosDraft: VinculosFiltrosDraft;
  setFiltrosDraft: React.Dispatch<React.SetStateAction<VinculosFiltrosDraft>>;
  onFiltrar: () => void;
  onLimparFiltros: () => void;
  // Vínculos Encerrados não tem mais ligação com a empresa nesse momento —
  // essa tela esconde o campo (ver VinculosEncerradosRedesign).
  mostrarFiltroEmpresa?: boolean;
  // Só Vínculos Ativos: a rota de Encerrados não aceita `ordenar_por`.
  mostrarOrdenacaoCompetencia?: boolean;
  // Só Vínculos Ativos: `status_negociacao` existe só na rota de CCBs ativas.
  mostrarFiltroStatusNegociacao?: boolean;
  mostrarFiltroRenegociacao?: boolean;
};

export default function VinculosFiltersBar({
  filtrosDraft,
  setFiltrosDraft,
  onFiltrar,
  onLimparFiltros,
  mostrarFiltroEmpresa = true,
  mostrarOrdenacaoCompetencia = false,
  mostrarFiltroStatusNegociacao = false,
  mostrarFiltroRenegociacao = false,
}: Props) {
  const ordenarPor = filtrosDraft.ordenarPor ?? "data_contratacao";

  return (
    <Flex flexDirection="column" gap={4} width="100%">
      <Flex gap={4} flexDirection={{ base: "column", md: "row" }}>
        <Box flex={1}>
          <Tooltip label="Filtrar por Tomador" fontSize="md" placement="bottom">
            <Input
              placeholder="Tomador"
              bg="white"
              height="3rem"
              color="black"
              fontFamily="Lexend Deca"
              fontSize="1rem"
              value={filtrosDraft.tomador}
              onChange={(e) =>
                setFiltrosDraft((prev) => ({ ...prev, tomador: e.target.value }))
              }
            />
          </Tooltip>
        </Box>

        {mostrarFiltroEmpresa && (
          <Box flex={1}>
            <Tooltip label="Filtrar por Empresa" fontSize="md" placement="bottom">
              <Input
                placeholder="Empresa"
                bg="white"
                height="3rem"
                color="black"
                fontFamily="Lexend Deca"
                fontSize="1rem"
                value={filtrosDraft.empresa}
                onChange={(e) =>
                  setFiltrosDraft((prev) => ({ ...prev, empresa: e.target.value }))
                }
              />
            </Tooltip>
          </Box>
        )}

        <Box flex={1}>
          <Tooltip label="Filtrar por CCB/ShortHash" fontSize="md" placement="bottom">
            <Input
              placeholder="CCB/ShortHash"
              bg="white"
              height="3rem"
              color="black"
              fontFamily="Lexend Deca"
              fontSize="1rem"
              value={filtrosDraft.ccb}
              onChange={(e) =>
                setFiltrosDraft((prev) => ({ ...prev, ccb: e.target.value }))
              }
            />
          </Tooltip>
        </Box>
      </Flex>

      <Flex width="100%" alignItems="center" justifyContent="flex-end" gap={4} flexWrap="wrap">
        {/* O cabeçalho é color="white"; o select e as <option> herdariam isso e
            sumiriam no fundo branco do dropdown — cor explícita, como os inputs. */}
        {mostrarFiltroStatusNegociacao && (
          <Tooltip label="Filtrar por status da negociação" fontSize="md" placement="bottom">
            <Select
              {...selectStyle}
              value={filtrosDraft.statusNegociacao ?? ""}
              onChange={(e) =>
                setFiltrosDraft((prev) => ({
                  ...prev,
                  statusNegociacao: e.target.value as NegociacaoStatusEnum | "",
                }))
              }
            >
              <option value="">Negociação: todas</option>
              <option value={NegociacaoStatusEnum.PENDENTE}>Pendente</option>
              <option value={NegociacaoStatusEnum.EM_ANDAMENTO}>Em andamento</option>
              <option value={NegociacaoStatusEnum.CONCLUIDA}>Concluída</option>
            </Select>
          </Tooltip>
        )}
        {mostrarFiltroRenegociacao && (
          <Tooltip label="Filtrar por situação da renegociação" fontSize="md" placement="bottom">
            <Select
              {...selectStyle}
              value={filtrosDraft.renegociacao ?? ""}
              onChange={(e) =>
                setFiltrosDraft((prev) => ({
                  ...prev,
                  renegociacao: e.target.value as RenegociacaoVinculoEncerrado | "",
                }))
              }
            >
              <option value="">Renegociação: sem filtro</option>
              <option value="nunca_tentada">Nunca tentada</option>
              <option value="erro">Erro</option>
              <option value="averbada">Averbada</option>
            </Select>
          </Tooltip>
        )}
        {mostrarOrdenacaoCompetencia && (
          <Tooltip label="Ordenar por" fontSize="md" placement="bottom">
            <Select
              {...selectStyle}
              value={ordenarPor}
              onChange={(e) => {
                const novo = e.target.value as OrdenacaoVinculosEmpregador;
                // Competência abre pelo atraso mais antigo — é o que se procura.
                setFiltrosDraft((prev) => ({
                  ...prev,
                  ordenarPor: novo,
                  ordenacao: novo === "competencia" ? "ASC" : "DESC",
                }));
              }}
            >
              <option value="data_contratacao">Data de contratação</option>
              <option value="competencia">Data de Competência</option>
            </Select>
          </Tooltip>
        )}
        <Select
          {...selectStyle}
          value={filtrosDraft.ordenacao}
          onChange={(e) =>
            setFiltrosDraft((prev) => ({
              ...prev,
              ordenacao: e.target.value as "ASC" | "DESC",
            }))
          }
        >
          {ordenarPor === "competencia" ? (
            <>
              <option value="ASC">{ROTULOS_ORDENACAO[ordenarPor].ASC}</option>
              <option value="DESC">{ROTULOS_ORDENACAO[ordenarPor].DESC}</option>
            </>
          ) : (
            <>
              <option value="DESC">{ROTULOS_ORDENACAO[ordenarPor].DESC}</option>
              <option value="ASC">{ROTULOS_ORDENACAO[ordenarPor].ASC}</option>
            </>
          )}
        </Select>
        <Button variant="ghost" colorScheme="primary" onClick={onLimparFiltros}>
          Limpar Filtros
        </Button>
        <Button colorScheme="primary" onClick={onFiltrar}>
          Filtrar
        </Button>
      </Flex>
    </Flex>
  );
}
